/**
 * v6.34.4 — Maintenance audio notification utility.
 *
 * Synthesizes short notification chimes via the Web Audio API so the
 * operator hears a sound when a long-running maintenance action
 * (audit, re-verify, MIME-fix, bulk-fix, remediate, download) reaches
 * a terminal state. Two distinct tones:
 *
 *   - playSuccessChime(): C5 → E5 ascending sine wave (~300 ms total)
 *     → "happy" 2-note arpeggio for successful completion.
 *
 *   - playFailureChime(): A4 → F4 descending sine wave (~400 ms total)
 *     → "uh-oh" 2-note descending tone for failure / cancellation /
 *     watchdog-cancelled / stubborn-count-exceeded warning.
 *
 * Design choices:
 *   - Web Audio API synthesis (NO audio-asset files). Zero deploy
 *     complexity; works offline; ~70 LOC.
 *   - Idempotent AudioContext — one shared context per session,
 *     lazily created on first play, auto-resumed if the browser
 *     suspended it (autoplay policy: needs prior user gesture; since
 *     the operator clicked "Run", this is satisfied).
 *   - localStorage-backed mute preference under
 *     "mosaic_maintenance_audio_muted" so the operator's mute setting
 *     persists across page reloads.
 *   - Defensive: if AudioContext is unavailable (test environments,
 *     ancient browsers), every function silently no-ops — never
 *     throws.
 */

const STORAGE_KEY = "mosaic_maintenance_audio_muted";

let _ctx = null;

/** Idempotent AudioContext getter. Returns null if Web Audio is
 * unavailable. */
function _getContext() {
  if (_ctx) return _ctx;
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    _ctx = new Ctx();
  } catch {
    return null;
  }
  return _ctx;
}

/** True if the operator has muted maintenance audio. */
export function isMaintenanceAudioMuted() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Persist mute preference. */
export function setMaintenanceAudioMuted(muted) {
  if (typeof window === "undefined") return;
  try {
    if (muted) {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* localStorage disabled — silently ignore. */
  }
}

/** Play a single sine-wave note. Internal helper for the chime
 * functions; not exported because the public API is the two
 * named chimes below. */
function _playNote(ctx, freqHz, startSec, durationSec, peakGain = 0.18) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freqHz;
  // ADSR-ish envelope: 8 ms attack, 8 ms decay-to-sustain, sustain,
  // ~60 ms release. Prevents clicks at start/end.
  const t0 = ctx.currentTime + startSec;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peakGain, t0 + 0.008);
  gain.gain.linearRampToValueAtTime(peakGain * 0.7, t0 + 0.016);
  gain.gain.setValueAtTime(peakGain * 0.7, t0 + durationSec - 0.06);
  gain.gain.linearRampToValueAtTime(0, t0 + durationSec);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + durationSec + 0.01);
}

/** Internal — auto-resume the AudioContext if the browser suspended it.
 * Returns true if the context is in a playable state. */
async function _ensureRunning(ctx) {
  if (!ctx) return false;
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }
  return ctx.state === "running";
}

/** Success — ascending 2-note arpeggio (C5 → E5, sine, ~300 ms). */
export async function playSuccessChime() {
  if (isMaintenanceAudioMuted()) return;
  const ctx = _getContext();
  if (!ctx) return;
  if (!(await _ensureRunning(ctx))) return;
  // C5 = 523.25 Hz; E5 = 659.25 Hz.
  _playNote(ctx, 523.25, 0.0, 0.13);
  _playNote(ctx, 659.25, 0.12, 0.18);
}

/** Failure — descending 2-note tone (A4 → F4, sine, ~400 ms). */
export async function playFailureChime() {
  if (isMaintenanceAudioMuted()) return;
  const ctx = _getContext();
  if (!ctx) return;
  if (!(await _ensureRunning(ctx))) return;
  // A4 = 440 Hz; F4 = 349.23 Hz.
  _playNote(ctx, 440.0, 0.0, 0.18, 0.22);
  _playNote(ctx, 349.23, 0.18, 0.22, 0.22);
}

/** Test helper — synchronous play of BOTH chimes back-to-back so the
 * operator can preview them when toggling mute off. */
export async function previewChimes() {
  await playSuccessChime();
  // Failure plays after success finishes (~340 ms cushion).
  setTimeout(playFailureChime, 500);
}
